/** @format */

/**
 * Trace Span 拓扑统一入口：按服务类型选择对应拓扑生成器。
 *
 * 1. Langfuse（WorkflowGraph）→ langfuseWorkflowTopology
 * 2. LangGraph（Span Attributes）→ langgraphSpanTopology
 * 3. 通用 OTEL Span 树 → spanTreeTopology
 */

import type { TraceAnalysisDetailResponse, TraceAnalysisDetailSpan } from '../../typescript'
import { buildSpanChildrenMap, isLangGraphTrace } from '../langgraphSpanTree'
import { buildLangGraphSpanTopology } from './langgraphSpanTopology'
import type { LangGraphSpanEdge } from './langgraphSpanTopology'
import {
  buildLangfuseWorkflowTopology,
  getLangfuseDefaultSelectableSpan,
  hasLangfuseWorkflowGraph
} from './langfuseWorkflowTopology'
import type { LangfuseWorkflowEdge, LangfuseWorkflowNode } from './langfuseWorkflowTopology'
import { buildSpanTreeTopology, collectVisibleSpanIds } from './spanTreeTopology'
import type { TopologyLayoutDirection, TopologyLayoutOptions, TopologySpanNode } from './types'

export type {
  LangfuseWorkflowEdge,
  LangfuseWorkflowExecutionItem,
  LangfuseWorkflowNode,
  LangfuseWorkflowTopology
} from './langfuseWorkflowTopology'
export {
  buildLangfuseWorkflowTopology,
  buildWorkflowGraphTopology,
  findWorkflowExecutionItem,
  getLangfuseDefaultSelectableSpan,
  getNextWorkflowExecutionIndex,
  hasLangfuseWorkflowGraph,
  hasWorkflowGraph
} from './langfuseWorkflowTopology'
export type {
  LangGraphSpanEdge,
  LangGraphSpanTopology,
  LangGraphTopology,
  LangGraphTopologyLayoutOptions
} from './langgraphSpanTopology'
export {
  buildLangGraphSpanTopology,
  buildLangGraphTopology,
  buildLangGraphTopologyLayout,
  decideLangGraphLayoutDirection
} from './langgraphSpanTopology'
export { buildSpanTreeTopology, collectVisibleSpanIds } from './spanTreeTopology'
export type { SpanTreeTopology } from './spanTreeTopology'
export type { TopologyLayoutDirection, TopologyLayoutOptions, TopologySpanNode } from './types'

export type TraceTopologyModel =
  | {
      mode: 'langfuse'
      nodes: LangfuseWorkflowNode[]
      edges: LangfuseWorkflowEdge[]
      layoutDirection: TopologyLayoutDirection
    }
  | {
      mode: 'langGraph'
      nodes: TopologySpanNode[]
      edges: LangGraphSpanEdge[]
      layoutDirection: TopologyLayoutDirection
      childrenMap: Map<string, TraceAnalysisDetailSpan[]>
      visibleSpanIds: Set<string>
    }
  | {
      mode: 'spanTree'
      nodes: TopologySpanNode[]
      layoutDirection: TopologyLayoutDirection
      childrenMap: Map<string, TraceAnalysisDetailSpan[]>
      visibleSpanIds: Set<string>
    }

/** @deprecated 使用 mode: 'langfuse' */
export type TopologyModel = TraceTopologyModel

export function hasTraceTopologyData(
  detail: TraceAnalysisDetailResponse | null | undefined,
  spans: TraceAnalysisDetailSpan[]
): boolean {
  return spans.length > 0 || hasLangfuseWorkflowGraph(detail)
}

/** 详情页默认选中的 Span：Langfuse 跳过 virtual 起止节点，其余取列表首项 */
export function getDefaultSelectableSpan(
  detail: TraceAnalysisDetailResponse | null | undefined,
  spans: TraceAnalysisDetailSpan[]
): TraceAnalysisDetailSpan | undefined {
  if (!spans.length) return undefined
  if (hasLangfuseWorkflowGraph(detail)) {
    return getLangfuseDefaultSelectableSpan(detail.WorkflowGraph, spans) ?? spans[0]
  }
  return spans[0]
}

export function buildTraceTopologyModel(
  detail: TraceAnalysisDetailResponse | null | undefined,
  spans: TraceAnalysisDetailSpan[],
  collapsedSpanIds: Set<string>,
  layoutOptions: TopologyLayoutOptions = {}
): TraceTopologyModel {
  // 优先级：Langfuse WorkflowGraph > LangGraph Attributes > 普通 Span 树
  if (hasLangfuseWorkflowGraph(detail)) {
    const { nodes, edges, layoutDirection } = buildLangfuseWorkflowTopology(
      detail.WorkflowGraph,
      spans,
      layoutOptions
    )
    return { mode: 'langfuse', nodes, edges, layoutDirection }
  }

  const childrenMap = buildSpanChildrenMap(spans)
  const visibleSpanIds = collectVisibleSpanIds(childrenMap, collapsedSpanIds)

  if (isLangGraphTrace(spans)) {
    const visibleSpans = spans.filter(span => visibleSpanIds.has(span.SpanId))
    const { edges, positions, layoutDirection } = buildLangGraphSpanTopology(
      visibleSpans,
      layoutOptions
    )
    const nodes: TopologySpanNode[] = []
    visibleSpans.forEach(span => {
      const pos = positions.get(span.SpanId)
      if (pos) nodes.push({ span, x: pos.x, y: pos.y })
    })
    const visibleEdges = edges.filter(
      edge => visibleSpanIds.has(edge.from) && visibleSpanIds.has(edge.to)
    )
    return {
      mode: 'langGraph',
      nodes,
      edges: visibleEdges,
      layoutDirection,
      childrenMap,
      visibleSpanIds
    }
  }

  const { nodes, layoutDirection } = buildSpanTreeTopology(
    childrenMap,
    collapsedSpanIds,
    layoutOptions
  )
  return {
    mode: 'spanTree',
    nodes,
    layoutDirection,
    childrenMap,
    visibleSpanIds
  }
}
